import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, User, Folder, Zap, Briefcase, Mail, LogOut, ArrowUpRight, Settings, Building2, Award, Sparkles, MessageSquareQuote, HelpCircle, Cog } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const navItems = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/admin/profile", label: "Profile & Hero", icon: User, testid: "nav-profile" },
    { to: "/admin/projects", label: "Projects", icon: Folder, testid: "nav-projects" },
    { to: "/admin/services", label: "Services", icon: Sparkles, testid: "nav-services" },
    { to: "/admin/skills", label: "Skills", icon: Zap, testid: "nav-skills" },
    { to: "/admin/experiences", label: "Experiences", icon: Briefcase, testid: "nav-experiences" },
    { to: "/admin/brands", label: "Clients & Partners", icon: Building2, testid: "nav-brands" },
    { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, testid: "nav-testimonials" },
    { to: "/admin/certifications", label: "Certifications", icon: Award, testid: "nav-certifications" },
    { to: "/admin/faqs", label: "FAQ", icon: HelpCircle, testid: "nav-faqs" },
    { to: "/admin/submissions", label: "Submissions", icon: Mail, testid: "nav-submissions" },
    { to: "/admin/settings", label: "Site Settings", icon: Cog, testid: "nav-settings" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/admin/login");
    };

    return (
        <div data-testid="admin-layout" className="min-h-screen bg-ink-900 text-white grid lg:grid-cols-[260px_1fr]">
            <aside className="border-r border-white/5 bg-ink-900 lg:sticky lg:top-0 lg:h-screen flex flex-col">
                <div className="p-6 border-b border-white/5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center">
                        <span className="font-display text-lg">A</span>
                    </div>
                    <div>
                        <div className="font-display text-base">Admin Studio</div>
                        <div className="mono-label" style={{ fontSize: 9 }}>akhmadferdian.com</div>
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-1">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={it.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive
                                        ? "bg-brand/15 text-brand border border-brand/30"
                                        : "text-ink-200 hover:text-white hover:bg-white/5 border border-transparent"
                                }`
                            }
                        >
                            <it.icon size={16} />
                            <span>{it.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/5 space-y-2">
                    <a
                        href="/"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-view-site"
                        className="flex items-center justify-between px-3.5 py-2.5 rounded-md text-sm text-ink-200 hover:text-white hover:bg-white/5"
                    >
                        <span className="inline-flex items-center gap-3">
                            <Settings size={16} /> View live site
                        </span>
                        <ArrowUpRight size={14} />
                    </a>
                    <div className="px-3.5 py-3 rounded-md bg-ink-800/60 border border-white/5">
                        <div className="text-xs text-ink-300 font-mono">Signed in as</div>
                        <div className="text-sm text-white truncate" data-testid="admin-user-email">{user?.email}</div>
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout"
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm text-ink-200 hover:text-white hover:bg-white/5"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>

            <main className="p-6 lg:p-10 max-w-full">
                <Outlet />
            </main>
        </div>
    );
}
